import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Link, Navigate, useParams, useLocation } from 'react-router-dom';
import Header from './components/common/Header.jsx';

// Lazy-load all pages so each route only downloads what it needs.
// chess.js + react-chessboard are chunked separately and only fetched
// when a user visits a tournament or game page.
const HomePage           = lazy(() => import('./pages/HomePage.jsx'));
const TournamentDetailPage = lazy(() => import('./pages/TournamentDetailPage.jsx'));
const GameViewerPage     = lazy(() => import('./pages/GameViewerPage.jsx'));
const GeoLandingPage     = lazy(() => import('./pages/GeoLandingPage.jsx'));
const TopPlayersPage     = lazy(() => import('./pages/TopPlayersPage.jsx'));
// PlayerProfilePage is now served as a static Astro page via Vercel proxy rewrite.
// Force a hard navigation so the browser hits Vercel (not React Router).
const PlayerProfilePage = () => {
  const { gender, fideId } = useParams();
  useEffect(() => {
    window.location.replace(`/players/${gender}/${fideId}`);
  }, [gender, fideId]);
  return <div className="py-16 text-center text-sm text-zinc-400">Loading…</div>;
};
const CandidatesPage     = lazy(() => import('./pages/CandidatesPage.jsx'));
const CandidatesBetaPage = lazy(() => import('./pages/CandidatesBetaPage.jsx'));
const CandidatesPairingsPage = lazy(() => import('./pages/CandidatesPairingsPage.jsx'));
const AboutPage          = lazy(() => import('./pages/AboutPage.jsx'));
import BackToTop from './components/common/BackToTop.jsx';
import HreflangTags from './components/common/HreflangTags.jsx';
import { useLanguage } from './i18n/LanguageContext.jsx';
import { initAnalytics, trackPageView } from './services/analytics.js';

const BlogRedirect = () => {
  useEffect(() => { window.location.href = 'https://www.shatranj.live/blogs'; }, []);
  return <div className="py-16 text-center text-sm text-zinc-400">Redirecting to blog…</div>;
};

const TournamentIdRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/tournament/${id}`} replace />;
};

const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    trackPageView(path);
  }, [location.pathname, location.search]);
  return null;
};

function App() {
  const { t } = useLanguage();
  useEffect(() => {
    initAnalytics();
  }, []);
  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 transition-colors duration-200">
      {/* Skip to main content — issue 18 */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-[200] focus:px-4 focus:py-2 focus:bg-white dark:focus:bg-zinc-900 focus:text-zinc-900 dark:focus:text-zinc-100 focus:rounded-lg focus:shadow-lg focus:text-sm focus:font-medium focus:border focus:border-zinc-200 dark:focus:border-zinc-700"
      >
        Skip to main content
      </a>
      <RouteTracker />
      <HreflangTags />
      <Header />
      <main id="main-content" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <Suspense fallback={<div className="py-16 text-center text-sm text-zinc-400 dark:text-zinc-500">Loading…</div>}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/india" element={<GeoLandingPage region="india" />} />
          <Route path="/us" element={<GeoLandingPage region="us" />} />
          <Route path="/russia" element={<GeoLandingPage region="russia" />} />
          <Route path="/china" element={<GeoLandingPage region="china" />} />
          <Route path="/norway" element={<GeoLandingPage region="norway" />} />
          <Route path="/germany" element={<GeoLandingPage region="germany" />} />
          <Route path="/azerbaijan" element={<GeoLandingPage region="azerbaijan" />} />
          <Route path="/france" element={<GeoLandingPage region="france" />} />
          <Route path="/netherlands" element={<GeoLandingPage region="netherlands" />} />
          <Route path="/uzbekistan" element={<GeoLandingPage region="uzbekistan" />} />
          <Route path="/armenia" element={<GeoLandingPage region="armenia" />} />
          <Route path="/spain" element={<GeoLandingPage region="spain" />} />
          <Route path="/ind" element={<Navigate to="/india" replace />} />
          <Route path="/candidates" element={<CandidatesBetaPage />} />
          <Route path="/candidates/standings" element={<CandidatesBetaPage defaultTab="standings" />} />
          <Route path="/candidates/games" element={<CandidatesBetaPage defaultTab="games" />} />
          <Route path="/candidates/pairings" element={<CandidatesPairingsPage />} />
          <Route path="/candidates-beta" element={<Navigate to="/candidates" replace />} />
          <Route path="/candidates-old" element={<CandidatesPage />} />
          <Route path="/players" element={<TopPlayersPage />} />
          <Route path="/players/:gender/:fideId" element={<PlayerProfilePage />} />
          <Route path="/tournament/:id" element={<TournamentDetailPage />} />
          {/* /tournaments/:id → /tournament/:id (singular) */}
          <Route path="/tournaments/:id" element={<TournamentIdRedirect />} />
          <Route path="/game" element={<GameViewerPage />} />
          {/* /blog → www.shatranj.live/blogs (proxied via vercel.json) */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogRedirect />} />
          <Route path="/blog/*" element={<BlogRedirect />} />
        </Routes>
        </Suspense>
      </main>
      <footer className="border-t border-zinc-200 dark:border-zinc-800 mt-16 py-8 text-xs text-zinc-400">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-6">
            {/* Brand */}
            <div className="flex flex-col items-center sm:items-start gap-2">
              <img src="/logo-wordmark-transparent.png" alt="Shatranj" className="h-5 w-auto block dark:hidden opacity-50" />
              <img src="/logo-wordmark-dark-transparent.png" alt="Shatranj" className="h-5 w-auto hidden dark:block opacity-50" />
              <p className="text-zinc-400 dark:text-zinc-500 text-center sm:text-left">
                Live FIDE chess tournament tracker
              </p>
              <a
                href="https://twitter.com/shatranjlive"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
              >
                @shatranjlive
              </a>
            </div>

            {/* Links */}
            <div className="flex flex-wrap justify-center sm:justify-end gap-x-6 gap-y-2">
              <div className="flex flex-col gap-1.5">
                <span className="text-zinc-500 dark:text-zinc-400 font-medium uppercase tracking-wide text-[10px]">Track</span>
                <Link to="/players" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">Players</Link>
                <a href="/blogs" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">Blog</a>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-zinc-500 dark:text-zinc-400 font-medium uppercase tracking-wide text-[10px]">Countries</span>
                <Link to="/india" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">{t('india')}</Link>
                <Link to="/us" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">{t('us')}</Link>
                <Link to="/russia" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">Russia</Link>
                <Link to="/china" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">China</Link>
                <Link to="/norway" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">Norway</Link>
                <Link to="/germany" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">Germany</Link>
                <Link to="/netherlands" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">Netherlands</Link>
                <Link to="/uzbekistan" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">Uzbekistan</Link>
                <Link to="/france" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">France</Link>
                <Link to="/azerbaijan" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">Azerbaijan</Link>
                <Link to="/armenia" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">Armenia</Link>
                <Link to="/spain" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">Spain</Link>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-zinc-500 dark:text-zinc-400 font-medium uppercase tracking-wide text-[10px]">Read</span>
                <a href="https://www.shatranj.live/blogs" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">Blog</a>
                <a href="https://www.shatranj.live/guides/" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">Guides</a>
                <a href="https://www.shatranj.live/blogs/feed.json" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">RSS / JSON Feed</a>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-zinc-500 dark:text-zinc-400 font-medium uppercase tracking-wide text-[10px]">Company</span>
                <Link to="/about" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">About</Link>
                <a href="mailto:shatranjlive@gmail.com" className="hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors">Contact</a>
              </div>
            </div>
          </div>

          <p className="text-center text-zinc-400 dark:text-zinc-600 mt-6">
            © {new Date().getFullYear()} Shatranj Live · Data sourced from FIDE &amp; Lichess Broadcasts
          </p>
        </div>
      </footer>
      <BackToTop />
    </div>
  );
}

export default App;
